export type BallType =
  | "golf"
  | "soccer"
  | "tennis"
  | "volleyball"
  | "football"
  | "basketball"
  | "badminton"
  | "baseball";

export const BALL_TYPES: readonly BallType[] = [
  "golf",
  "soccer",
  "tennis",
  "volleyball",
  "football",
  "basketball",
  "badminton",
  "baseball",
];

export const BALL_SCALE: Record<BallType, number> = {
  golf: 0.4,
  tennis: 0.52,
  badminton: 0.42,
  baseball: 0.56,
  volleyball: 0.96,
  soccer: 1.0,
  basketball: 1.09,
  football: 0.94,
};

type SoccerFace = readonly [number, readonly (readonly [number, number])[]];

const SOCCER_FACES: readonly SoccerFace[] = [
  [
    6,
    [
      [-0.202, 0.73],
      [0.202, 0.73],
      [0.404, 0.855],
      [0.202, 0.979],
      [-0.202, 0.979],
      [-0.404, 0.855],
    ],
  ],
  [
    6,
    [
      [-0.404, -0.855],
      [-0.202, -0.979],
      [0.202, -0.979],
      [0.404, -0.855],
      [0.202, -0.73],
      [-0.202, -0.73],
    ],
  ],
  [
    5,
    [
      [0.855, -0.326],
      [0.979, 0],
      [0.855, 0.326],
      [0.653, 0.202],
      [0.653, -0.202],
    ],
  ],
  [
    5,
    [
      [-0.653, -0.202],
      [-0.653, 0.202],
      [-0.855, 0.326],
      [-0.979, 0],
      [-0.855, -0.326],
    ],
  ],
  [
    6,
    [
      [0.404, 0.855],
      [0.202, 0.73],
      [0.326, 0.404],
      [0.653, 0.202],
      [0.855, 0.326],
      [0.73, 0.653],
    ],
  ],
  [
    6,
    [
      [-0.73, 0.653],
      [-0.855, 0.326],
      [-0.653, 0.202],
      [-0.326, 0.404],
      [-0.202, 0.73],
      [-0.404, 0.855],
    ],
  ],
  [
    6,
    [
      [0.73, -0.653],
      [0.855, -0.326],
      [0.653, -0.202],
      [0.326, -0.404],
      [0.202, -0.73],
      [0.404, -0.855],
    ],
  ],
  [
    6,
    [
      [-0.404, -0.855],
      [-0.202, -0.73],
      [-0.326, -0.404],
      [-0.653, -0.202],
      [-0.855, -0.326],
      [-0.73, -0.653],
    ],
  ],
  [
    5,
    [
      [-0.326, 0.404],
      [0, 0.202],
      [0.326, 0.404],
      [0.202, 0.73],
      [-0.202, 0.73],
    ],
  ],
  [
    5,
    [
      [-0.202, -0.73],
      [0.202, -0.73],
      [0.326, -0.404],
      [0, -0.202],
      [-0.326, -0.404],
    ],
  ],
  [
    6,
    [
      [0.653, -0.202],
      [0.653, 0.202],
      [0.326, 0.404],
      [0, 0.202],
      [0, -0.202],
      [0.326, -0.404],
    ],
  ],
  [
    6,
    [
      [-0.326, -0.404],
      [0, -0.202],
      [0, 0.202],
      [-0.326, 0.404],
      [-0.653, 0.202],
      [-0.653, -0.202],
    ],
  ],
];

type SoccerEdge = readonly [readonly [number, number], readonly [number, number]];

const SOCCER_SEAMS: readonly SoccerEdge[] = buildSoccerSeams();

function buildSoccerSeams(): readonly SoccerEdge[] {
  const useCount = new Map<string, number>();
  const edgeByKey = new Map<string, SoccerEdge>();
  for (const [, points] of SOCCER_FACES) {
    for (let index = 0; index < points.length; index++) {
      const from = points[index];
      const to = points[(index + 1) % points.length];
      const key = [from, to]
        .map((point) => `${point[0].toFixed(3)},${point[1].toFixed(3)}`)
        .sort()
        .join("|");
      useCount.set(key, (useCount.get(key) ?? 0) + 1);
      edgeByKey.set(key, [from, to]);
    }
  }
  return [...useCount.entries()].filter(([, count]) => count > 1).map(([key]) => edgeByKey.get(key) as SoccerEdge);
}
export function traceBallBody(context: CanvasRenderingContext2D, type: BallType, radius: number): void {
  context.beginPath();
  if (type === "football") {
    context.moveTo(-radius * 1.34, 0);
    context.bezierCurveTo(-radius * 0.75, -radius * 1.05, radius * 0.75, -radius * 1.05, radius * 1.34, 0);
    context.bezierCurveTo(radius * 0.75, radius * 1.05, -radius * 0.75, radius * 1.05, -radius * 1.34, 0);
    context.closePath();
    return;
  }
  if (type === "badminton") {
    context.moveTo(-radius * 0.34, radius * 0.3);
    context.lineTo(-radius * 0.92, -radius * 0.95);
    context.quadraticCurveTo(0, -radius * 0.66, radius * 0.92, -radius * 0.95);
    context.lineTo(radius * 0.34, radius * 0.3);
    context.closePath();
    context.moveTo(radius * 0.36, radius * 0.42);
    context.arc(0, radius * 0.42, radius * 0.36, 0, Math.PI * 2);
    return;
  }
  context.arc(0, 0, radius, 0, Math.PI * 2);
}

export function traceBallMarks(context: CanvasRenderingContext2D, type: BallType, radius: number): void {
  switch (type) {
    case "golf":
      return traceGolfMarks(context, radius);
    case "soccer":
      return traceSoccerMarks(context, radius);
    case "tennis":
      return traceTennisMarks(context, radius);
    case "volleyball":
      return traceVolleyballMarks(context, radius);
    case "basketball":
      return traceBasketballMarks(context, radius);
    case "baseball":
      return traceBaseballMarks(context, radius);
    case "football":
      return traceFootballMarks(context, radius);
    case "badminton":
      return traceBadmintonMarks(context, radius);
    default:
      return undefined;
  }
}

function traceGolfMarks(context: CanvasRenderingContext2D, radius: number): void {
  const step = radius * 0.34;
  for (let gridY = -2; gridY <= 2; gridY++) {
    for (let gridX = -2; gridX <= 2; gridX++) {
      const x = (gridX + (gridY & 1 ? 0.5 : 0)) * step;
      const y = gridY * step * 0.88;
      if (Math.hypot(x, y) > radius * 0.74) {
        continue;
      }
      context.beginPath();
      context.arc(x, y, radius * 0.07, 0, Math.PI * 2);
      context.fill();
    }
  }
}

function traceSoccerMarks(context: CanvasRenderingContext2D, radius: number): void {
  for (const [sides, points] of SOCCER_FACES) {
    if (sides !== 5) {
      continue;
    }
    context.beginPath();
    for (let index = 0; index < points.length; index++) {
      const x = points[index][0] * radius;
      const y = points[index][1] * radius;
      if (index === 0) {
        context.moveTo(x, y);
      } else {
        context.lineTo(x, y);
      }
    }
    context.closePath();
    context.fill();
  }
  for (const [from, to] of SOCCER_SEAMS) {
    context.beginPath();
    context.moveTo(from[0] * radius, from[1] * radius);
    context.lineTo(to[0] * radius, to[1] * radius);
    context.stroke();
  }
}

function traceTennisMarks(context: CanvasRenderingContext2D, radius: number): void {
  for (const side of [-1, 1]) {
    context.beginPath();
    context.moveTo(side * radius * 0.62, -radius * 0.785);
    context.bezierCurveTo(
      side * radius * 0.1,
      -radius * 0.35,
      side * radius * 0.1,
      radius * 0.35,
      side * radius * 0.62,
      radius * 0.785
    );
    context.stroke();
  }
}

function traceVolleyballMarks(context: CanvasRenderingContext2D, radius: number): void {
  const curve = (x0: number, y0: number, cx: number, cy: number, x1: number, y1: number): void => {
    context.beginPath();
    context.moveTo(x0, y0);
    context.quadraticCurveTo(cx, cy, x1, y1);
    context.stroke();
  };
  const verticalSeam = (offset: number): void => {
    const half = Math.sqrt(Math.max(0, radius * radius - offset * offset)) * 0.96;
    curve(offset, -half, offset * 1.34, 0, offset, half);
  };
  const horizontalSeam = (y: number): void => {
    const half = Math.sqrt(Math.max(0, radius * radius - y * y)) * 0.96;
    curve(-half * 0.99, y, -half * 0.6, y * 1.15, radius * 0.1, y * 0.92);
  };

  context.save();
  context.rotate(0.3);
  verticalSeam(radius * 0.08);
  verticalSeam(radius * 0.42);
  verticalSeam(radius * 0.68);
  horizontalSeam(-radius * 0.55);
  horizontalSeam(0);
  horizontalSeam(radius * 0.55);
  context.restore();
}

function traceBasketballMarks(context: CanvasRenderingContext2D, radius: number): void {
  context.beginPath();
  context.moveTo(0, -radius);
  context.lineTo(0, radius);
  context.stroke();
  context.beginPath();
  context.moveTo(-radius, 0);
  context.lineTo(radius, 0);
  context.stroke();
  context.beginPath();
  context.arc(-radius * 1.35, 0, radius * 0.95, -0.75, 0.75);
  context.stroke();
  context.beginPath();
  context.arc(radius * 1.35, 0, radius * 0.95, Math.PI - 0.75, Math.PI + 0.75);
  context.stroke();
}

function traceBaseballMarks(context: CanvasRenderingContext2D, radius: number): void {
  const stitchCount = 7;
  const stitchHalfLength = radius * 0.1;
  const stitchHalfWidth = radius * 0.07;

  for (const side of [-1, 1]) {
    for (let index = 0; index < stitchCount; index++) {
      const progress = (index + 0.5) / stitchCount;
      const x = cubicBezier(side * 0.62, side * 0.1, side * 0.1, side * 0.62, progress) * radius;
      const y = cubicBezier(-0.785, -0.35, 0.35, 0.785, progress) * radius;
      const slopeX = cubicBezierSlope(side * 0.62, side * 0.1, side * 0.1, side * 0.62, progress);
      const slopeY = cubicBezierSlope(-0.785, -0.35, 0.35, 0.785, progress);
      const length = Math.hypot(slopeX, slopeY);
      const tangentX = slopeX / length;
      const tangentY = slopeY / length;
      const normalX = -tangentY;
      const normalY = tangentX;
      context.beginPath();
      context.moveTo(
        x + normalX * stitchHalfLength + tangentX * stitchHalfWidth,
        y + normalY * stitchHalfLength + tangentY * stitchHalfWidth
      );
      context.lineTo(
        x - normalX * stitchHalfLength - tangentX * stitchHalfWidth,
        y - normalY * stitchHalfLength - tangentY * stitchHalfWidth
      );
      context.stroke();
    }
  }
}

function traceFootballMarks(context: CanvasRenderingContext2D, radius: number): void {
  context.beginPath();
  context.moveTo(-radius * 0.55, 0);
  context.lineTo(radius * 0.55, 0);
  context.stroke();
  for (let index = -2; index <= 2; index++) {
    context.beginPath();
    context.moveTo(index * radius * 0.2, -radius * 0.22);
    context.lineTo(index * radius * 0.2, radius * 0.22);
    context.stroke();
  }
  for (const side of [-1, 1]) {
    context.beginPath();
    const start = side > 0 ? Math.PI - 0.9 : -0.9;
    const end = side > 0 ? Math.PI + 0.9 : 0.9;
    context.arc(side * radius * 1.05, 0, radius * 0.42, start, end);
    context.stroke();
  }
}

function traceBadmintonMarks(context: CanvasRenderingContext2D, radius: number): void {
  context.beginPath();
  context.moveTo(-radius * 0.36, radius * 0.11);
  context.lineTo(radius * 0.36, radius * 0.11);
  context.stroke();
  for (const featherX of [-0.62, -0.21, 0.21, 0.62]) {
    context.beginPath();
    context.moveTo(featherX * radius * 0.34, radius * 0.11);
    context.lineTo(featherX * radius * 1.35, -radius * 0.9);
    context.stroke();
  }
}

function cubicBezier(a: number, b: number, c: number, d: number, t: number): number {
  const u = 1 - t;
  return u * u * u * a + 3 * u * u * t * b + 3 * u * t * t * c + t * t * t * d;
}

function cubicBezierSlope(a: number, b: number, c: number, d: number, t: number): number {
  const u = 1 - t;
  return 3 * u * u * (b - a) + 6 * u * t * (c - b) + 3 * t * t * (d - c);
}
